import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { AuthContext } from "../context/AuthContext";
import { ErrorContext } from "../context/ErrorContext";
import axiosInstance from "../utils/axiosInstance";
import CommanderPicker from "../components/commanders/CommanderPicker";
import { AppButton, PageShell, SectionCard } from "../components/ui";
import "./css/Market.css";

const ORDER_TYPES = [
  { value: "", label: "All types" },
  { value: "sell", label: "Selling" },
  { value: "buy", label: "Buying" },
];

const ORDER_STATUSES = [
  { value: "", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "matched", label: "Matched" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "expired", label: "Expired" },
];

const EMPTY_FORM = {
  type: "sell",
  status: "open",
  title: "",
  nation: "",
  sellerNation: "",
  buyerNation: "",
  commander: "",
  sellerCommander: "",
  buyerCommander: "",
  askingPrice: "",
  targetBudget: "",
  agreedPrice: "",
  finalSalePrice: "",
  currency: "USD",
  notes: "",
};

const formatMoney = (value, currency = "USD") => {
  if (value === undefined || value === null || value === "") return "N/A";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 0,
  }).format(numeric);
};

const formatNumber = (value) => (
  value !== undefined && value !== null && Number.isFinite(Number(value))
    ? Number(value).toLocaleString()
    : "N/A"
);

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
};

const commanderLabel = (commander) => {
  if (!commander) return "Unattached";
  return `${commander.name || "Unknown"}${commander.cityLevel ? ` C${commander.cityLevel}` : ""}`;
};

const getOrderPriceLabel = (order) => {
  if (order.status === "completed") return `Sold for ${formatMoney(order.finalSalePrice, order.currency)}`;
  if (order.status === "pending" || order.status === "matched") return `Agreed ${formatMoney(order.agreedPrice, order.currency)}`;
  if (order.type === "buy") return `Budget ${formatMoney(order.targetBudget, order.currency)}`;
  return `Ask ${formatMoney(order.askingPrice, order.currency)}`;
};

const normalizeForm = (order) => ({
  type: order?.type || "sell",
  status: order?.status || "open",
  title: order?.title || "",
  nation: order?.nation?._id || order?.nation || "",
  sellerNation: order?.sellerNation?._id || order?.sellerNation || order?.commander?.nation?._id || order?.commander?.nation || order?.nation?._id || order?.nation || "",
  buyerNation: order?.buyerNation?._id || order?.buyerNation || order?.buyerCommander?.nation?._id || order?.buyerCommander?.nation || "",
  commander: order?.commander?._id || order?.commander || "",
  sellerCommander: order?.sellerCommander?._id || order?.sellerCommander || order?.commander?._id || order?.commander || "",
  buyerCommander: order?.buyerCommander?._id || order?.buyerCommander || "",
  askingPrice: order?.askingPrice ?? "",
  targetBudget: order?.targetBudget ?? "",
  agreedPrice: order?.agreedPrice ?? "",
  finalSalePrice: order?.finalSalePrice ?? "",
  currency: order?.currency || "USD",
  notes: order?.notes || "",
});

const NationSelect = ({
  label,
  nations,
  value,
  onChange,
  placeholder = "Search nation",
  required = false,
  allOption = false,
  floatingLabel = false,
}) => {
  const options = useMemo(() => {
    const base = allOption ? [{ _id: "", number: "All nations", label: "All nations" }] : [];
    return [
      ...base,
      ...nations.map((nation) => ({
        ...nation,
        label: `#${nation.number}`,
      })),
    ];
  }, [allOption, nations]);

  const selectedValue = options.find((option) => String(option._id || "") === String(value || "")) || null;

  return (
    <Autocomplete
      className="market-nation-select"
      options={options}
      value={selectedValue}
      onChange={(_, option) => onChange(option?._id || "")}
      getOptionLabel={(option) => option?.label || (option?.number ? `#${option.number}` : "")}
      isOptionEqualToValue={(option, selected) => String(option?._id || "") === String(selected?._id || "")}
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          label={floatingLabel ? label : undefined}
          placeholder={placeholder}
          required={required}
        />
      )}
    />
  );
};

const Market = () => {
  const { user, isAuthInitializing } = useContext(AuthContext);
  const { showError, showInfo } = useContext(ErrorContext);
  const navigate = useNavigate();
  const isSuperUser = String(user?.role || "") === "superUser";

  const [orders, setOrders] = useState([]);
  const [nations, setNations] = useState([]);
  const [filters, setFilters] = useState({ type: "", status: "", nationId: "", search: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalOrder, setModalOrder] = useState(undefined);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (isAuthInitializing) return;
    if (!isSuperUser) navigate("/home");
  }, [isAuthInitializing, isSuperUser, navigate]);

  const nationOptions = useMemo(
    () => [...nations].sort((a, b) => Number(a.number || 0) - Number(b.number || 0)),
    [nations]
  );

  const loadNations = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/nations", { params: { includeInactive: true } });
      setNations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      showError(error);
    }
  }, [showError]);

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      Object.entries({
        type: filters.type,
        status: filters.status,
        nationId: filters.nationId,
      }).forEach(([key, value]) => {
        if (String(value || "").trim()) params[key] = String(value).trim();
      });
      const response = await axiosInstance.get("/market-orders", { params });
      setOrders(Array.isArray(response.data?.orders) ? response.data.orders : []);
    } catch (error) {
      showError(error);
    } finally {
      setIsLoading(false);
    }
  }, [filters.type, filters.status, filters.nationId, showError]);

  useEffect(() => {
    if (!isSuperUser) return;
    loadNations();
  }, [isSuperUser, loadNations]);

  useEffect(() => {
    if (!isSuperUser) return;
    loadOrders();
  }, [isSuperUser, filters.type, filters.status, filters.nationId, loadOrders]);

  const openCreateModal = () => {
    setModalOrder(null);
    setForm(EMPTY_FORM);
  };

  const openEditModal = (order) => {
    setModalOrder(order);
    setForm(normalizeForm(order));
  };

  const closeModal = () => {
    setModalOrder(undefined);
    setForm(EMPTY_FORM);
  };

  const updateForm = (patch) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const submitOrder = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      commander: form.commander || null,
      buyerCommander: form.buyerCommander || null,
      askingPrice: form.askingPrice === "" ? null : Number(form.askingPrice),
      targetBudget: form.targetBudget === "" ? null : Number(form.targetBudget),
      agreedPrice: form.agreedPrice === "" ? null : Number(form.agreedPrice),
      finalSalePrice: form.finalSalePrice === "" ? null : Number(form.finalSalePrice),
    };

    setIsSaving(true);
    try {
      const response = modalOrder
        ? await axiosInstance.patch(`/market-orders/${modalOrder._id}`, payload)
        : await axiosInstance.post("/market-orders", payload);
      const savedOrder = response.data?.order;
      if (savedOrder) {
        setOrders((current) => {
          const exists = current.some((order) => String(order._id) === String(savedOrder._id));
          return exists
            ? current.map((order) => (String(order._id) === String(savedOrder._id) ? savedOrder : order))
            : [savedOrder, ...current];
        });
      }
      showInfo(modalOrder ? "Market order updated." : "Market order created.");
      closeModal();
    } catch (error) {
      showError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Delete market order for ${commanderLabel(order.commander)}?`)) return;
    try {
      await axiosInstance.delete(`/market-orders/${order._id}`);
      setOrders((current) => current.filter((item) => String(item._id) !== String(order._id)));
      showInfo("Market order deleted.");
    } catch (error) {
      showError(error);
    }
  };

  const displayedOrders = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    if (!search) return orders;
    return orders.filter((order) => {
      const haystack = [
        order.title,
        order.notes,
        order.commander?.name,
        order.buyerCommander?.name,
        order.sellerCommander?.name,
        order.alliance?.acronym,
        order.nation?.number,
      ].join(" ").toLowerCase();
      return haystack.includes(search);
    });
  }, [orders, filters.search]);

  const summary = useMemo(() => {
    return displayedOrders.reduce((acc, order) => {
      acc.total += 1;
      acc[order.status] = (acc[order.status] || 0) + 1;
      if (order.status === "completed" && Number.isFinite(Number(order.finalSalePrice))) {
        acc.completedValue += Number(order.finalSalePrice);
      }
      return acc;
    }, { total: 0, completedValue: 0 });
  }, [displayedOrders]);

  if (!isSuperUser) return null;

  const isModalOpen = modalOrder !== undefined;

  return (
    <PageShell maxWidth="xl">
      <div className="market-page">
        <div className="market-header">
          <div>
            <h1>Market</h1>
            <p>Super-user market order tracking for city sale and purchase intent.</p>
          </div>
          <AppButton onClick={openCreateModal}>Create Order</AppButton>
        </div>

        <div className="market-summary-grid">
          <SectionCard className="market-summary-card">
            <span>Total Orders</span>
            <strong>{formatNumber(summary.total)}</strong>
          </SectionCard>
          <SectionCard className="market-summary-card">
            <span>Open</span>
            <strong>{formatNumber(summary.open || 0)}</strong>
          </SectionCard>
          <SectionCard className="market-summary-card">
            <span>Pending</span>
            <strong>{formatNumber(summary.pending || 0)}</strong>
          </SectionCard>
          <SectionCard className="market-summary-card">
            <span>Completed Value</span>
            <strong>{formatMoney(summary.completedValue)}</strong>
          </SectionCard>
        </div>

        <SectionCard className="market-filter-card">
          <div className="market-filters">
            <label>
              Type
              <select value={filters.type} onChange={(e) => setFilters((current) => ({ ...current, type: e.target.value }))}>
                {ORDER_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <label>
              Status
              <select value={filters.status} onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))}>
                {ORDER_STATUSES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <div className="market-field">
              Nation
              <NationSelect
                label="Nation"
                nations={nationOptions}
                value={filters.nationId}
                onChange={(value) => setFilters((current) => ({ ...current, nationId: value }))}
                allOption
              />
            </div>
            <label>
              Search
              <input
                value={filters.search}
                onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                placeholder="Commander, alliance, notes"
              />
            </label>
            <AppButton kind="secondary" onClick={loadOrders} disabled={isLoading}>
              {isLoading ? "Loading..." : "Refresh"}
            </AppButton>
          </div>
        </SectionCard>

        <div className="market-order-grid">
          {displayedOrders.map((order) => (
            <SectionCard key={order._id} className={`market-order-card market-order-card--${order.status}`}>
              <div className="market-order-topline">
                <div>
                  <span className={`market-pill market-pill--${order.type}`}>{order.type === "buy" ? "Buying" : "Selling"}</span>
                  <span className={`market-pill market-pill--${order.status}`}>{order.status}</span>
                </div>
                <strong>{getOrderPriceLabel(order)}</strong>
              </div>
              <h3>{order.title || commanderLabel(order.commander)}</h3>
              <div className="market-order-meta">
                <span>Seller #{order.sellerNation?.number ?? order.nation?.number ?? "N/A"}</span>
                <span>Buyer #{order.buyerNation?.number ?? "N/A"}</span>
                <span>{order.alliance?.acronym ? `(${order.alliance.acronym})` : "No alliance"}</span>
                <span>Updated {formatDate(order.updatedAt)}</span>
              </div>
              <div className="market-asset-row">
                <div>
                  <span>City</span>
                  <strong>{commanderLabel(order.commander)}</strong>
                  <small>BP {formatNumber(order.commander?.battlePower)} | {order.commander?.cityRole || "unknown"}</small>
                </div>
                <div>
                  <span>Buyer</span>
                  <strong>{commanderLabel(order.buyerCommander)}</strong>
                  <small>Budget {formatMoney(order.targetBudget, order.currency)}</small>
                </div>
              </div>
              {order.notes ? <p className="market-order-notes">{order.notes}</p> : null}
              <div className="market-order-actions">
                <AppButton kind="secondary" onClick={() => openEditModal(order)}>Edit</AppButton>
                <AppButton kind="secondary" onClick={() => deleteOrder(order)} sx={{ color: "#9f2a2a", borderColor: "#e3b5b5" }}>Delete</AppButton>
              </div>
            </SectionCard>
          ))}
        </div>

        {!displayedOrders.length && (
          <SectionCard className="market-empty">
            {isLoading ? "Loading market orders..." : "No market orders match the current filters."}
          </SectionCard>
        )}

        {isModalOpen && (
          <div className="market-modal-backdrop" onClick={closeModal}>
            <form className="market-modal" onSubmit={submitOrder} onClick={(event) => event.stopPropagation()}>
              <div className="market-modal-header">
                <div>
                  <h2>{modalOrder ? "Edit Market Order" : "Create Market Order"}</h2>
                  <p>{form.type === "sell" ? "Track a city being offered for sale." : "Track buying intent or a matched buyer."}</p>
                </div>
                <button type="button" onClick={closeModal} aria-label="Close">&times;</button>
              </div>

              <div className="market-modal-body">
                <section className="market-form-section">
                  <div className="market-form-section-head">
                    <h3>Order Details</h3>
                    <p>Define the order type and current transaction state.</p>
                  </div>
                  <div className="market-form-section-grid market-form-section-grid--three">
                    <label>
                      Type
                      <select
                        value={form.type}
                        onChange={(e) => {
                          const nextType = e.target.value;
                          updateForm({
                            type: nextType,
                            nation: nextType === "sell" ? form.sellerNation : form.buyerNation,
                          });
                        }}
                      >
                        <option value="sell">Selling</option>
                        <option value="buy">Buying</option>
                      </select>
                    </label>
                    <label>
                      Status
                      <select value={form.status} onChange={(e) => updateForm({ status: e.target.value })}>
                        {ORDER_STATUSES.filter((option) => option.value).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Title
                      <input value={form.title} onChange={(e) => updateForm({ title: e.target.value })} placeholder="Optional label" />
                    </label>
                  </div>
                </section>

                <div className="market-parties-grid">
                  <section className="market-form-section">
                    <div className="market-form-section-head">
                      <h3>Seller / City</h3>
                      <p>Attach the city that is being sold or tracked.</p>
                    </div>
                    <div className="market-form-section-grid">
                      <div className="market-field">
                        <span className="market-label-row">
                          Seller Nation {form.type === "sell" ? <span className="market-required">Required</span> : null}
                        </span>
                        <NationSelect
                          label="Seller Nation"
                          nations={nationOptions}
                          value={form.sellerNation}
                          onChange={(value) => updateForm({
                            sellerNation: value,
                            nation: form.type === "sell" ? value : form.nation,
                            commander: "",
                            sellerCommander: "",
                          })}
                          placeholder="Search seller nation"
                          required={form.type === "sell"}
                        />
                        <span className="market-form-help">Select a nation first, then choose the city commander from that nation.</span>
                      </div>
                      <label className="market-picker-label market-picker-label--full">
                        <span className="market-label-row">
                          City Commander {form.type === "sell" ? <span className="market-required">Required</span> : <span className="market-optional">Optional</span>}
                        </span>
                        <CommanderPicker
                          value={form.commander}
                          onChange={(value, option) => updateForm({
                            commander: value,
                            sellerCommander: value,
                            sellerNation: option?.nationId || form.sellerNation,
                            nation: option?.nationId || form.sellerNation || form.nation,
                          })}
                          mode="nation"
                          scopeId={form.sellerNation}
                          disabled={!form.sellerNation}
                          limit={120}
                          placeholder={form.type === "sell" ? "Select city commander" : "Select matched city"}
                        />
                      </label>
                    </div>
                  </section>

                  <section className="market-form-section">
                    <div className="market-form-section-head">
                      <h3>Buyer</h3>
                      <p>Use this for buy orders or matched buyers.</p>
                    </div>
                    <div className="market-form-section-grid">
                      <div className="market-field">
                        <span className="market-label-row">
                          Buyer Nation {form.type === "buy" ? <span className="market-required">Required</span> : <span className="market-optional">Optional</span>}
                        </span>
                        <NationSelect
                          label="Buyer Nation"
                          nations={nationOptions}
                          value={form.buyerNation}
                          onChange={(value) => updateForm({
                            buyerNation: value,
                            nation: form.type === "buy" ? value : form.nation,
                            buyerCommander: "",
                          })}
                          placeholder="Search buyer nation"
                          required={form.type === "buy"}
                        />
                      </div>
                      <label className="market-picker-label market-picker-label--full">
                        <span className="market-label-row">
                          Buyer Commander <span className="market-optional">Optional</span>
                        </span>
                        <CommanderPicker
                          value={form.buyerCommander}
                          onChange={(value, option) => updateForm({
                            buyerCommander: value,
                            buyerNation: option?.nationId || form.buyerNation,
                          })}
                          mode="nation"
                          scopeId={form.buyerNation}
                          disabled={!form.buyerNation}
                          limit={120}
                          placeholder="Select buyer commander"
                        />
                      </label>
                    </div>
                  </section>
                </div>

                <section className="market-form-section">
                  <div className="market-form-section-head">
                    <h3>Pricing</h3>
                    <p>Track ask, budget, agreed price, and final sale amount as the order moves forward.</p>
                  </div>
                  <div className="market-form-section-grid market-form-section-grid--pricing">
                    <label>
                      Asking Price
                      <input type="number" min="0" value={form.askingPrice} onChange={(e) => updateForm({ askingPrice: e.target.value })} placeholder="Seller ask" />
                    </label>
                    <label>
                      Target Budget
                      <input type="number" min="0" value={form.targetBudget} onChange={(e) => updateForm({ targetBudget: e.target.value })} placeholder="Buyer budget" />
                    </label>
                    <label>
                      Agreed Price
                      <input type="number" min="0" value={form.agreedPrice} onChange={(e) => updateForm({ agreedPrice: e.target.value })} placeholder="Pending deal" />
                    </label>
                    <label>
                      Final Sale Price
                      <input type="number" min="0" value={form.finalSalePrice} onChange={(e) => updateForm({ finalSalePrice: e.target.value })} placeholder="Completed sale" />
                    </label>
                    <label>
                      Currency
                      <input value={form.currency} onChange={(e) => updateForm({ currency: e.target.value.toUpperCase() })} maxLength={8} />
                    </label>
                  </div>
                </section>

                <section className="market-form-section">
                  <label className="market-form-span">
                    Notes
                    <textarea value={form.notes} onChange={(e) => updateForm({ notes: e.target.value })} rows={4} placeholder="Private market notes, buyer/seller context, payment state, or risk notes" />
                  </label>
                </section>
              </div>

              <div className="market-modal-actions">
                <AppButton type="button" kind="secondary" onClick={closeModal} disabled={isSaving}>Cancel</AppButton>
                <AppButton type="submit" disabled={isSaving}>{isSaving ? "Saving..." : "Save Order"}</AppButton>
              </div>
            </form>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default Market;
